import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import CartItem from '../components/cart/CartItem';
import { formatCurrency } from '../utils/formatCurrency';
import { 
  ShoppingBag, 
  ArrowRight, 
  ArrowLeft, 
  Truck, 
  Tag, 
  ShieldCheck, 
  Sparkles, 
  RotateCcw,
  AlertTriangle,
  Trash2,
  X 
} from 'lucide-react';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, getCartTotal } = useCart();

  // Coupon / Promo State
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');

  // Delete Confirmation Modal State
  const [itemToDelete, setItemToDelete] = useState(null);

  const subtotal = getCartTotal();

  // Free express insured shipping threshold (₹25,000)
  const freeShippingThreshold = 25000;
  const progressToFreeShipping = Math.min(100, (subtotal / freeShippingThreshold) * 100);
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - subtotal);

  const handleApplyPromo = (e) => {
    e.preventDefault();
    if (promoCode.trim().toUpperCase() === 'VEDHYAS10') {
      const calculatedDiscount = subtotal * 0.10;
      setDiscount(calculatedDiscount);
      setPromoApplied(true);
      setPromoError('');
      
      toast.success('Promo code VEDHYAS10 applied (10% OFF)!', {
        icon: '✨',
      });
    } else {
      setPromoError('Invalid code. Try "VEDHYAS10" for 10% off.');
      toast.error('Invalid Promo Code');
    }
  };

  // Confirm and process item removal matching exact variant
  const handleConfirmDelete = () => {
    if (itemToDelete) {
      removeFromCart(itemToDelete.id, itemToDelete.selectedColor);
      setItemToDelete(null);
    }
  };

  const grandTotal = Math.max(0, subtotal - discount);

  /* --- 1. EMPTY CART STATE --- */
  if (!cart || cart.length === 0) {
    return (
      <main className="py-16 sm:py-24 text-center max-w-xl mx-auto px-4 bg-[#FDFBF7]">
        <div className="bg-[#FDFBF7] rounded-3xl p-10 sm:p-12 border border-[#E8DFC8] shadow-sm space-y-6">
          <div className="w-16 h-16 bg-[#F4EFE6] text-[#4A0E17] rounded-full flex items-center justify-center mx-auto border border-[#E8DFC8]">
            <ShoppingBag className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-serif font-bold text-[#4A0E17]">
              Your Shopping Bag is Empty
            </h2>
            <p className="text-xs text-[#544842] font-light leading-relaxed">
              Explore our curated collections of pure silk sarees and fine jewelry to discover handcrafted royalty.
            </p>
          </div>
          <button
            onClick={() => navigate('/catalog')}
            className="inline-flex items-center space-x-2 bg-[#4A0E17] hover:bg-[#6B1320] text-[#FDFBF7] text-xs font-serif font-bold uppercase tracking-wider px-8 py-3.5 rounded-2xl transition-all shadow-md active:scale-95 border border-[#6B1320]"
          >
            <span>Explore Collections</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    );
  }

  /* --- 2. POPULATED CART LAYOUT --- */
  return (
    <main className="py-6 sm:py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative bg-[#FDFBF7]">
      
      {/* Header & Back Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-[#E8DFC8]">
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <Sparkles className="w-3.5 h-3.5 text-[#4A0E17]" />
            <span className="text-[10px] font-serif font-bold tracking-[0.25em] text-[#4A0E17] uppercase">
              Curated Selection
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-[#4A0E17]">
            Shopping Bag ({cart.reduce((acc, item) => acc + item.quantity, 0)})
          </h1>
        </div>

        <button
          onClick={() => navigate('/catalog')}
          className="inline-flex items-center space-x-2 text-xs font-serif font-bold text-[#4A0E17] hover:text-[#6B1320] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Continue Shopping</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* LEFT COLUMN: ITEM LIST & FREE SHIPPING PROGRESS */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* Shipping Incentive Progress Bar */}
          <div className="bg-[#F4EFE6] border border-[#E8DFC8] rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="font-semibold text-[#4A0E17] flex items-center space-x-2">
                <Truck className="w-4 h-4 text-[#4A0E17] shrink-0" />
                <span>
                  {remainingForFreeShipping > 0
                    ? `Add ${formatCurrency(remainingForFreeShipping)} more to qualify for Free Insured Delivery`
                    : 'You unlocked Free Insured Express Delivery!'}
                </span>
              </span>
              <span className="font-bold text-[#4A0E17]">
                {Math.round(progressToFreeShipping)}%
              </span>
            </div>
            <div className="w-full bg-[#E8DFC8] h-2 rounded-full overflow-hidden">
              <div
                className="bg-[#4A0E17] h-full transition-all duration-500 rounded-full"
                style={{ width: `${progressToFreeShipping}%` }}
              />
            </div>
          </div>

          {/* Cart Items List */}
          <div className="bg-[#FDFBF7] rounded-3xl p-4 sm:p-6 border border-[#E8DFC8] shadow-sm divide-y divide-[#E8DFC8]">
            {cart.map((item) => (
              <CartItem
                key={`${item.id}-${item.selectedColor?.name || item.selectedColor || 'default'}`}
                item={item}
                onUpdateQuantity={updateQuantity}
                onRemove={() => setItemToDelete(item)}
              />
            ))}
          </div>

        </div>

        {/* RIGHT COLUMN: SUMMARY & CHECKOUT */}
        <div className="lg:col-span-4">
          <div className="bg-[#FDFBF7] rounded-3xl p-6 border border-[#E8DFC8] shadow-sm space-y-6 sticky top-24">
            
            <h3 className="text-base font-serif font-bold text-[#4A0E17] pb-4 border-b border-[#E8DFC8]">
              Order Summary
            </h3>

            {/* Promo Code Input */}
            <form onSubmit={handleApplyPromo} className="space-y-2">
              <label className="text-xs font-serif font-bold text-[#7A6B5D] flex items-center space-x-1.5">
                <Tag className="w-3.5 h-3.5 text-[#4A0E17]" />
                <span>Promo Code</span>
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="e.g. VEDHYAS10"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  disabled={promoApplied}
                  className="flex-1 text-xs px-3 py-2.5 bg-[#F4EFE6] border border-[#E8DFC8] rounded-xl focus:outline-none focus:border-[#4A0E17] uppercase font-mono text-[#4A0E17]"
                />
                <button
                  type="submit"
                  disabled={promoApplied || !promoCode}
                  className="bg-[#4A0E17] hover:bg-[#6B1320] disabled:opacity-50 text-[#FDFBF7] text-xs font-serif font-bold px-4 py-2.5 rounded-xl transition"
                >
                  Apply
                </button>
              </div>
              {promoApplied && (
                <p className="text-[11px] text-emerald-800 font-semibold">
                  Promo code applied (10% off)!
                </p>
              )}
              {promoError && (
                <p className="text-[11px] text-[#8B1E2D] font-medium">{promoError}</p>
              )}
            </form>

            {/* Price Calculations */}
            <div className="space-y-3 pt-4 border-t border-[#E8DFC8] text-xs">
              <div className="flex justify-between text-[#7A6B5D]">
                <span>Subtotal</span>
                <span className="font-bold text-[#4A0E17]">{formatCurrency(subtotal)}</span>
              </div>

              {promoApplied && (
                <div className="flex justify-between text-emerald-800">
                  <span>Discount</span>
                  <span className="font-bold">-{formatCurrency(discount)}</span>
                </div>
              )}

              <div className="flex justify-between text-[#7A6B5D]">
                <span>Insured Express Shipping</span>
                <span className="font-semibold text-[#4A0E17]">
                  {remainingForFreeShipping === 0 ? 'FREE' : 'Calculated at Checkout'}
                </span>
              </div>

              <div className="flex justify-between text-base font-serif font-bold text-[#4A0E17] pt-3 border-t border-[#E8DFC8]">
                <span>Total</span>
                <span className="text-[#4A0E17]">{formatCurrency(grandTotal)}</span>
              </div>
            </div>

            {/* Checkout Action Button */}
            <button
              onClick={() => navigate('/checkout')}
              className="w-full bg-[#4A0E17] hover:bg-[#6B1320] text-[#FDFBF7] py-4 rounded-2xl text-xs font-serif font-bold uppercase tracking-wider flex items-center justify-center space-x-2 transition-all shadow-md active:scale-95 border border-[#6B1320]"
            >
              <span>Proceed to Checkout</span>
              <ArrowRight className="w-4 h-4" />
            </button>

            {/* Security Assurances */}
            <div className="pt-4 border-t border-[#E8DFC8] space-y-2 text-[11px] text-[#7A6B5D]">
              <div className="flex items-center space-x-2">
                <ShieldCheck className="w-4 h-4 text-[#4A0E17]" />
                <span>256-Bit Encrypted SSL Checkout</span>
              </div>
              <div className="flex items-center space-x-2">
                <RotateCcw className="w-4 h-4 text-[#4A0E17]" />
                <span>100% Pure Silk & Certified Gems Guarantee</span>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* --- DELETE ITEM CONFIRMATION MODAL --- */}
      {itemToDelete && (
        <div className="fixed inset-0 z-50 bg-[#20050A]/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#FDFBF7] border border-[#E8DFC8] rounded-3xl p-6 max-w-sm w-full shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-[#E8DFC8] pb-3">
              <div className="flex items-center space-x-2 text-[#8B1E2D]">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="font-serif font-bold text-base text-[#4A0E17]">
                  Remove Item?
                </h3>
              </div>
              <button
                onClick={() => setItemToDelete(null)}
                className="p-1 text-[#7A6B5D] hover:text-[#4A0E17] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-[#544842] leading-relaxed">
              Are you sure you want to remove <strong className="text-[#4A0E17]">{itemToDelete.name}</strong> from your shopping bag?
            </p>

            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                onClick={() => setItemToDelete(null)}
                className="px-4 py-2 text-xs font-semibold text-[#7A6B5D] hover:bg-[#F4EFE6] rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-xs font-semibold bg-[#8B1E2D] hover:bg-[#6B1320] text-[#FDFBF7] rounded-xl shadow-sm transition-colors flex items-center space-x-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Remove</span>
              </button>
            </div>
          </div>
        </div>
      )}

    </main>
  );
};

export default CartPage;